// Clockify in de app: uren per taak, lopende timers, mijn week, capaciteit per persoon per week, en de koppelingen
// tussen Planner en Clockify. De server leest uit een kopie die de worker bijhoudt (timers elke minuut, uren elke
// 5 min, taken en verlof elk kwartier). Uren zijn getallen in uren; tijdstippen zijn ISO-tekst.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { call } from "./api";
import { useToast } from "../shell/Toast";

// ---------- types ----------

export interface ClockifyStatus {
  enabled: boolean;
  /** uit: geen sleutel; wacht: nog geen ronde; ok; fout */
  state: "uit" | "wacht" | "ok" | "fout";
  lastOk: string | null;
  /** laatste keer dat de lopende timers gelezen werden (elke minuut) */
  timersAt: string | null;
  error: string;
  errorKind: "" | "toegang" | "fout";
  /** aantal Clockify-gebruikers uit de eigen domeinen */
  users: number;
}

/** Eén Clockify-taak van een Planner-taak; shared = de andere open Planner-taken die ze delen. */
export interface ClockifyTaskHours {
  clockifyTaskId: string;
  missing?: boolean;
  name?: string;
  projectId?: string;
  project?: string;
  client?: string;
  estimate?: number | null;
  logged?: number;
  rest?: number | null;
  done?: boolean;
  shared: { taskId: string; title: string }[];
}

/**
 * Uren van een Planner-taak die aan Clockify-taken gekoppeld is (of bewust niet). Koppelingen zijn n-op-n: tasks zijn
 * haar Clockify-taken; de velden hieronder tellen ze samen (naam en project van de eerste). Een gedeelde Clockify-taak
 * telt hier volledig mee, in de bezetting maar één keer.
 */
export interface TaskHours {
  /** de eerste Clockify-taak */
  clockifyTaskId: string;
  tasks?: ClockifyTaskHours[];
  /** bewust geen Clockify-taak (Koppelingen) */
  none?: boolean;
  /** de gekoppelde Clockify-taak bestaat niet meer */
  missing?: boolean;
  name?: string;
  /** het Clockify-project van de Clockify-taak */
  projectId?: string;
  project?: string;
  client?: string;
  /** schatting in uren; null = geen schatting */
  estimate?: number | null;
  /** alle gelogde uren op de Clockify-taak, van iedereen */
  logged?: number;
  /** schatting min gelogd (nooit negatief); null zonder schatting */
  rest?: number | null;
  done?: boolean;
}

export interface Timer {
  personId: string;
  since: string;
  /** loopt langer dan 12 u: wellicht vergeten */
  stale: boolean;
  /** de Planner-taak: waarop de timer in de app startte, of de enige aan die Clockify-taak */
  taskId: string | null;
  /** alle open Planner-taken aan die Clockify-taak (ze kan gedeeld zijn) */
  taskIds?: string[];
  clockifyTaskId: string | null;
  taskName: string;
  project: string;
  client: string;
  description: string;
}

export interface MyWeekDay {
  date: string;
  cap: number;
  logged: number;
  /** verlof, feestdag of sluiting; null = gewone dag */
  absent: "verlof" | "feestdag" | "sluiting" | null;
  label: string | null;
}

export interface MyWeek {
  days: MyWeekDay[];
  logged: number;
  cap: number;
  /** gelogd deze week per Clockify-taak (of "p:<projectId>" zonder taak) */
  byClockifyTask: Record<string, number>;
}

export interface PlanHours {
  clockifyProjectId: string;
  name: string;
  client: string;
  /** alle gelogde uren op het Clockify-project */
  logged: number;
  /** som van de schattingen van de Clockify-taken */
  estimate: number;
  /** gelogd op de taken met een schatting (te vergelijken met estimate) */
  loggedOnEstimated: number;
  /** gelogd op taken met "support" in de naam */
  support: number;
}

export interface ClockifyLive {
  now: string;
  timers: Timer[];
  /** personId -> einde van de laatste registratie */
  lastActive: Record<string, string>;
  /** personen die in Clockify gevonden zijn (op e-mailadres) */
  linked: string[];
  /** Planner-taakId -> uren */
  tasks: Record<string, TaskHours>;
  /** de week van de aangemelde gebruiker; null als die niet in Clockify staat */
  week: MyWeek | null;
  /** Planner-klantplan (projectId) -> uren */
  plans: Record<string, PlanHours>;
  /** fase -> het Clockify-project dat de planner er rechtstreeks aan koppelde; geldt voor alles eronder */
  faseProjects?: Record<string, FaseProject>;
  clockify: ClockifyStatus;
}

/** Het Clockify-project van een fase (FaseLink): nieuwe Clockify-taken van de taken eronder komen daarin. */
export interface FaseProject {
  clockifyProjectId: string;
  name: string;
  client: string;
}

export interface Absence {
  kind: "verlof" | "feestdag" | "sluiting";
  label: string | null;
  from: string;
  to: string;
}

export interface CapWeek {
  /** beschikbare uren: werkcapaciteit min verlof, feestdagen en sluitingsdagen, maal inzetbaarheid */
  cap: number;
  /** capaciteit vanaf vandaag; enkel deze week kleiner dan cap */
  capLeft: number;
  /** geplande uren uit taken met een schatting (zonder achterstand en support) */
  load: number;
  /** gelogd in Clockify; enkel voor deze week, anders null */
  logged: number | null;
  absent: Absence[];
  /** taken die in deze week uren vragen, meeste eerst */
  tasks: { taskId: string; hours: number }[];
}

export interface CapPerson {
  personId: string;
  clockifyUserId: string | null;
  /** gewone werkweek (zonder afwezigheden), maal inzetbaarheid van deze week */
  weekHours: number;
  /** gemiddelde supporturen per week (laatste 8 weken) */
  supportPerWeek: number;
  /** rest van taken met een deadline in het verleden */
  backlog: { hours: number; tasks: { taskId: string; hours: number }[] };
  /** actieve taken van deze persoon zonder schatting */
  noEstimate: string[];
  weeks: CapWeek[];
}

export interface Capacity {
  today: string;
  weeks: { monday: string; label: string }[];
  people: CapPerson[];
  tasks: Record<string, TaskHours>;
  clockify: ClockifyStatus;
}

export interface PlanLinkRow {
  projectId: string;
  klant: string;
  linked: { clockifyProjectId: string; name: string; client: string; by: string; at: string } | null;
  suggestions: { clockifyProjectId: string; name: string; client: string; score: number }[];
}

export interface TaskLinkRow {
  taskId: string;
  projectId: string;
  title: string;
  category: string;
  /** het klantplan is aan een Clockify-project gekoppeld */
  planLinked: boolean;
  /** de eerste gekoppelde Clockify-taak; more = hoeveel er nog bij horen */
  linked: { clockifyTaskId: string; name: string; estimate: number | null; logged: number; by: string; at: string; more?: number } | null;
  /** bewust geen Clockify-taak */
  none: boolean;
  /** voorstellen op naam, zekerheid 0..1 */
  suggestions: { clockifyTaskId: string; name: string; estimate: number | null; score: number }[];
}

export interface ClockifyLinks {
  plans: PlanLinkRow[];
  tasks: TaskLinkRow[];
  /** Clockify-projectId -> zijn actieve taken (used: al gekoppeld aan een open Planner-taak; mag nog, n-op-n) */
  choices: Record<string, { clockifyTaskId: string; name: string; estimate: number | null; used: boolean }[]>;
  projects: { clockifyProjectId: string; name: string; client: string }[];
  people: { personId: string; clockifyUserId: string; name: string }[];
  clockify: ClockifyStatus;
  log: { id: number; at: string; user: string; action: string; text: string }[];
}

interface Changed { ok: boolean; text: string; changeId?: number; hours?: TaskHours }

/** Eén registratie op de Clockify-taak (tabblad Uren); mine = van uzelf: de omschrijving vult u hier aan. */
export interface HoursEntry {
  id: string;
  personId: string | null;
  name: string;
  initials: string;
  /** uur:minuut in Brussel */
  from: string;
  to: string;
  hours: number;
  description: string;
  mine: boolean;
  /** de Clockify-taak (een taak kan er meerdere hebben) */
  clockifyTaskId: string;
  taskName: string;
}

/** Gelogd per dag op de Clockify-taak, het nieuwste eerst; earlier = wat de app niet bewaart (ouder dan since). */
export interface TaskLog {
  days: { date: string; hours: number; entries: HoursEntry[] }[];
  earlier: number;
  since: string | null;
}

/** Een open Clockify-taak om te koppelen; used = de andere open Planner-taken waar ze al bij hoort (mag, n-op-n). */
export interface ClockifyChoice {
  clockifyTaskId: string;
  name: string;
  estimate: number | null;
  logged: number;
  score: number;
  suggest: boolean;
  used: string | null;
  /** hangt al aan deze taak */
  linked: boolean;
}

/** Een klant in Clockify met zijn open projecten; id leeg en naam leeg = de projecten zonder klant. */
export interface ClockifyClient {
  id: string;
  name: string;
  projects: { id: string; name: string }[];
}

/** Waar en hoe een nieuwe Clockify-taak komt: een bestaand project, of een nieuw (bij een bestaande of nieuwe klant). */
export interface NewClockifyTask {
  name?: string;
  projectId?: string;
  newProject?: string;
  clientId?: string;
  newClient?: string;
  /** een klantplan zonder Clockify-project krijgt dit project (planner) */
  linkPlan?: boolean;
}

// ---------- API ----------

export const clockifyApi = {
  live: () => call<ClockifyLive>("GET", "/api/clockify/live"),
  capacity: () => call<Capacity>("GET", "/api/capacity"),
  links: () => call<ClockifyLinks>("GET", "/api/clockify/links"),
  linkPlan: (projectId: string, clockifyProjectId: string) => call<Changed>("POST", "/api/clockify/plan-links", { projectId, clockifyProjectId }),
  /**
   * mode set (standaard, Koppelingen): de taak hangt enkel aan clockifyTaskId; "" = bewust geen, null = nergens aan.
   * add: een Clockify-taak erbij; remove: die ene eraf (n-op-n).
   */
  linkTasks: (links: { taskId: string; clockifyTaskId: string | null; mode?: "set" | "add" | "remove" }[], linkPlan?: boolean) =>
    call<Changed>("POST", "/api/clockify/task-links", { links, linkPlan }),
  createTask: (taskId: string, estimate?: number, how?: NewClockifyTask) => call<Changed>("POST", "/api/clockify/tasks", { taskId, estimate, ...how }),
  /** de naam van de gekoppelde Clockify-taak, in Clockify zelf */
  rename: (taskId: string, name: string, clockifyTaskId?: string) => call<Changed & { before: string }>("POST", "/api/clockify/task-name", { taskId, name, clockifyTaskId }),
  clients: () => call<{ clients: ClockifyClient[] }>("GET", "/api/clockify/klanten"),
  faseLinks: () => call<{ byFase: Record<string, FaseProject> }>("GET", "/api/clockify/fase-links"),
  /** planner: het Clockify-project van een fase zetten of (null) weghalen */
  setFaseLink: (faseId: string, clockifyProjectId: string | null) =>
    call<{ ok: boolean; text: string; changeId: number; byFase: Record<string, FaseProject> }>("POST", "/api/clockify/fase-links", { faseId, clockifyProjectId }),
  /** de open Clockify-taken van een project (anders dat van het klantplan) om aan deze taak te koppelen */
  choices: (taskId: string, project?: string) =>
    call<{ projectId: string; tasks: ClockifyChoice[] }>("GET", `/api/tasks/${taskId}/clockify-keuzes` + (project ? "?project=" + encodeURIComponent(project) : "")),
  log: (taskId: string) => call<TaskLog>("GET", `/api/tasks/${taskId}/uren`),
  /** 0 = schatting wissen; clockifyTaskId: welke, als de taak er meerdere heeft */
  estimate: (taskId: string, hours: number, clockifyTaskId?: string) => call<Changed>("POST", "/api/clockify/estimate", { taskId, hours, clockifyTaskId }),
  /** de omschrijving is verplicht: waaraan werkt u? clockifyTaskId: welke, als de taak er meerdere heeft */
  startTimer: (taskId: string, description: string, clockifyTaskId?: string) =>
    call<{ ok: boolean; live: ClockifyLive }>("POST", "/api/clockify/timer", { taskId, description, clockifyTaskId }),
  describeTimer: (description: string) => call<{ ok: boolean; text: string; live: ClockifyLive }>("PATCH", "/api/clockify/timer", { description }),
  describeEntry: (id: string, description: string) => call<{ ok: boolean; text: string }>("PATCH", `/api/clockify/entries/${id}`, { description }),
  stopTimer: () => call<{ ok: boolean; hours: number | null; live: ClockifyLive }>("POST", "/api/clockify/timer/stop", {}),
  /** uren achteraf bijschrijven voor uzelf; vandaag eindigt de registratie nu, een andere dag begint om 9 u */
  logHours: (taskId: string, date: string, hours: number, description?: string) =>
    call<Changed & { live: ClockifyLive }>("POST", "/api/clockify/entries", { taskId, date, hours, description }),
};

// ---------- hooks ----------

export function useClockifyLive() {
  return useQuery({ queryKey: ["clockify-live"], queryFn: clockifyApi.live, refetchInterval: 60e3, staleTime: 30e3 });
}

export function useCapacity(enabled = true) {
  return useQuery({ queryKey: ["capacity"], queryFn: clockifyApi.capacity, refetchInterval: 5 * 60e3, staleTime: 60e3, enabled });
}

export function useClockifyLinks(enabled = true) {
  return useQuery({ queryKey: ["clockify-links"], queryFn: clockifyApi.links, staleTime: 30e3, enabled });
}

/**
 * Timer starten (met de omschrijving: waaraan werkt u?), de omschrijving wijzigen terwijl hij loopt, en stoppen; voor
 * uzelf. Werkt de gedeelde live-gegevens meteen bij; na het stoppen ook de uren per dag van de taken.
 */
export function useTimer() {
  const qc = useQueryClient();
  const toast = useToast();
  const start = useMutation({
    mutationFn: ({ taskId, description, clockifyTaskId }: { taskId: string; title: string; description: string; clockifyTaskId?: string }) =>
      clockifyApi.startTimer(taskId, description, clockifyTaskId),
    onSuccess: (r, v) => { qc.setQueryData(["clockify-live"], r.live); toast.flash("Timer loopt op " + v.title + ": " + v.description); },
    onError: (e: Error) => toast.flash(e.message, { error: true }),
  });
  const describe = useMutation({
    mutationFn: (description: string) => clockifyApi.describeTimer(description),
    onSuccess: (r) => { qc.setQueryData(["clockify-live"], r.live); toast.flash(r.text); },
    onError: (e: Error) => toast.flash(e.message, { error: true }),
  });
  const stop = useMutation({
    mutationFn: () => clockifyApi.stopTimer(),
    onSuccess: (r) => {
      qc.setQueryData(["clockify-live"], r.live);
      qc.invalidateQueries({ queryKey: ["capacity"] });
      qc.invalidateQueries({ queryKey: ["uren"] });
      toast.flash(r.hours != null ? "Timer gestopt · " + fmtHours(r.hours) + " gelogd" : "Er liep geen timer");
    },
    onError: (e: Error) => toast.flash(e.message, { error: true }),
  });
  return {
    start: (taskId: string, title: string, description: string, clockifyTaskId?: string) => start.mutateAsync({ taskId, title, description, clockifyTaskId }),
    describe: (description: string) => describe.mutateAsync(description),
    stop: () => stop.mutate(),
    busy: start.isPending || stop.isPending || describe.isPending,
  };
}

/** Gelogd per dag op de Clockify-taak van deze taak (tabblad Uren). */
export function useTaskLog(taskId: string, enabled = true) {
  return useQuery({ queryKey: ["uren", taskId], queryFn: () => clockifyApi.log(taskId), refetchInterval: 5 * 60e3, staleTime: 30e3, enabled });
}

/** Na een schatting, koppeling of nieuwe Clockify-taak: alles wat uren toont opnieuw laden. */
export function useClockifyWrite() {
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: ({ run }: { run: () => Promise<Changed> }) => run(),
    onSuccess: (r) => {
      for (const k of ["clockify-live", "capacity", "clockify-links", "uren", "verloop", "clockify-klanten", "clockify-keuzes"]) qc.invalidateQueries({ queryKey: [k] });
      toast.flash(r.text);
    },
    onError: (e: Error) => toast.flash(e.message, { error: true }),
  });
  return { run: <T extends Changed>(run: () => Promise<T>) => m.mutateAsync({ run }) as Promise<T>, busy: m.isPending };
}

// ---------- rekenen en tonen ----------

/** 7,5 u · 12 u · 0,3 u */
export function fmtHours(h: number | null | undefined, digits = 1): string {
  if (h == null) return "–";
  const r = Math.round(h * 10 ** digits) / 10 ** digits;
  return r.toLocaleString("nl-BE", { maximumFractionDigits: digits }) + " u";
}

/** Timer-duur: "1 u 25 min" of "12 min" */
export function fmtSince(since: string, now = Date.now()): string {
  const min = Math.max(0, Math.round((now - new Date(since).getTime()) / 60e3));
  return min >= 60 ? Math.floor(min / 60) + " u " + String(min % 60).padStart(2, "0") + " min" : min + " min";
}

export interface CapOptions {
  /** achterstand verdelen over zoveel weken na deze week (0 = niet tonen) */
  backlogWeeks: number;
  /** support als vaste reservering per week meetellen */
  withSupport: boolean;
}

/**
 * Wat het scherm per week toont: load met achterstand en support volgens de keuzes, en het percentage van de capaciteit.
 * Deze week: al gelogd plus wat nog gepland is, tegenover de hele week; support enkel voor het deel dat nog komt.
 * De achterstand gaat over de N weken na deze week, zoals in het ontwerp.
 */
export function weekLoad(p: CapPerson, i: number, o: CapOptions): { load: number; cap: number; pct: number | null } {
  const w = p.weeks[i];
  const left = w.cap > 0 ? Math.min(1, (w.capLeft ?? w.cap) / w.cap) : 0;
  let load = w.load + (i === 0 ? w.logged ?? 0 : 0);
  if (o.backlogWeeks > 0 && i >= 1 && i <= o.backlogWeeks) load += p.backlog.hours / o.backlogWeeks;
  if (o.withSupport && w.cap > 0) load += Math.min(p.supportPerWeek, w.cap) * left;
  return { load, cap: w.cap, pct: w.cap > 0 ? Math.round((load / w.cap) * 100) : null };
}

/** Kleur van een cel volgens Instellingen (room, full, over in %): enkel afwijkingen kleuren. */
export function loadTone(pct: number | null, t: { room: number; full: number; over: number }): "afwezig" | "ruimte" | "normaal" | "vol" | "overvol" {
  if (pct == null) return "afwezig";
  if (pct > t.over) return "overvol";
  if (pct >= t.full) return "vol";
  if (pct < t.room) return "ruimte";
  return "normaal";
}

/**
 * Vrije uren van een persoon tussen twee dagen (Nieuwe taak, Wie kan dit opnemen): capaciteit min load per week, naar
 * rato van de werkdagen in de periode. Deze week telt enkel wat nog komt: capaciteit vanaf vandaag min wat nog gepland is.
 */
export function freeHours(p: CapPerson, weeks: Capacity["weeks"], from: string, to: string, o: CapOptions): number {
  let free = 0;
  weeks.forEach((wk, i) => {
    const end = addDaysIso(wk.monday, 6);
    const a = from > wk.monday ? from : wk.monday, b = to < end ? to : end;
    if (a > b) return;
    const w = p.weeks[i];
    let room: number, share: number;
    if (i === 0) {
      const left = w.cap > 0 ? Math.min(1, w.capLeft / w.cap) : 0;
      room = w.capLeft - w.load - (o.withSupport ? Math.min(p.supportPerWeek, w.cap) * left : 0);
      share = 1; // de periode begint ten vroegste vandaag
    } else {
      const { load, cap } = weekLoad(p, i, o);
      room = cap - load;
      share = Math.min(1, workdays(a, b) / 5);
    }
    free += Math.max(0, room) * share;
  });
  return Math.round(free * 10) / 10;
}

function addDaysIso(s: string, n: number): string {
  return new Date(Date.parse(s) + n * 864e5).toISOString().slice(0, 10);
}

/** maandag tot vrijdag tussen twee dagen, beide meegeteld */
function workdays(a: string, b: string): number {
  let n = 0;
  for (let d = a; d <= b; d = addDaysIso(d, 1)) if (new Date(d + "T12:00:00Z").getUTCDay() % 6 !== 0) n++;
  return n;
}
